import React, { useEffect, useState, useCallback, useRef } from "react";
import { Stage, Layer, Image, Rect } from "react-konva";
import ContextMenu from "../ContextMenu";
import TimeRect from "./TimeRect";
import Icon from "../Icon";

// icons
import { RiMapPinTimeLine } from "react-icons/ri";
import { GiPauseButton } from "react-icons/gi";

// this variable exists solely to get around the fact that
// React sometimes seems to forget what the current state is
let selection = null;

const TimeAssignCanvas = (props) => {
  const {
    background,
    canvasWidth,
    canvasHeight,
    scale,
    stageRef,
    bgRef,
    rects,
    setRects,
    triggers,
    points,
    getTime,
    iterations,
    workingIteration,
    colorPickerVisible,
    readFill,
    readOpacity,
    setListening,
    videoRef,
    wavesurferRef,
    seekFrame,
    chapterType,
    playbackRate,
  } = props;

  const [forceUpdate, setForceUpdate] = useState(0);

  const skipDistance = useRef();

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: null,
    y: null,
  });

  // useEffect(() => {
  //   console.log(rects);
  // }, [rects]);

  useEffect(() => {
    if (forceUpdate > 0)
      setRects(
        rects.map((rect, i) => {
          return { ...rect, id: i };
        })
      );
  }, [forceUpdate]);

  useEffect(() => {
    skipDistance.current = parseFloat(playbackRate);
  }, [playbackRate]);

  useEffect(() => {
    setForceUpdate(forceUpdate + 1);
  }, [workingIteration]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case "ArrowLeft": {
        if (chapterType === "audio") wavesurferRef.current.skip(-0.25 * skipDistance.current);
        else seekFrame(videoRef.current.getCurrentTime() - 0.033);
        break;
      }
      case "ArrowRight": {
        if (chapterType === "audio") wavesurferRef.current.skip(0.25 * skipDistance.current);
        else seekFrame(videoRef.current.getCurrentTime() + 0.033);
        break;
      }
      case "a": {
        if (selection) assignTime();
        break;
      }
      default:
        break;
    }
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.id !== "contextMenu") {
      hideContextMenu();
      if (!e.target.attrs.selected) {
        selectObject(e.target);
      }
    }
    //if the user right-clicks
    if (e.evt.button === 2) {
      if (e.target === stageRef.current || e.target === bgRef.current || e.target.attrs.name === "point") return;
      generateContextOptions(e);
      showContextMenu(e);
      return;
    }
  };

  const selectObject = (obj) => {
    selection = obj;
    setRects(
      rects.map((rect) => {
        if (rect.id === parseInt(obj.attrs.id)) {
          return {
            ...rect,
            selected: true,
          };
        } else {
          return {
            ...rect,
            selected: false,
          };
        }
      })
    );
  };

  const assignTime = () => {
    hideContextMenu();
    if (workingIteration === -1) {
      setRects(
        rects.map((rect) => {
          if (parseInt(selection.attrs.id) === rect.id) {
            return {
              ...rect,
              startTime: parseFloat(getTime()),
              selected: true,
            };
          } else return { ...rect, selected: false };
        })
      );
    } else {
      let temp = [...rects];
      let foundExisting = false;

      // I need to check if the rect has any iterations applied, and then check
      // if it already has an assignment to the currently selected iteration

      // check for any iterations
      if (temp[selection.attrs.id].iterations) {
        // go through all existing iterations in the selected rect
        for (let i = 0; i < temp[selection.attrs.id].iterations.length; i++) {
          // if there's already an entry for the currently selected iteration
          if (temp[selection.attrs.id].iterations[i].name === iterations[workingIteration]) {
            // assign current time to it
            temp[selection.attrs.id].iterations[i].startTime = parseFloat(getTime());
            foundExisting = true;
          }
        }
      }

      // if there are no iterations on this rect (if it was created before this feature was added)
      if (!temp[selection.attrs.id].iterations) {
        temp[selection.attrs.id].iterations = [
          {
            name: iterations[workingIteration],
            startTime: parseFloat(getTime()),
          },
        ];
        setRects(temp);
        return;
      }

      // if this is a new assignment under the selected iteration
      if (!foundExisting) {
        // push a new entry
        temp[selection.attrs.id].iterations.push({
          name: `${iterations[workingIteration]}`,
          startTime: parseFloat(getTime()),
        });
        setRects(temp);
      }

      console.log(temp);
      setRects(temp);
      // setRects(
      //   rects.map((rect) => {
      //     if (parseInt(selection.attrs.id) === rect.id) {
      //       return {
      //         ...rect,
      //         iterations: rect.iterations.map((iteration) => {
      //           if (iterations[workingIteration] === iteration)
      //             return parseFloat(getTime());
      //           else return iteration;
      //         }),
      //         selected: true,
      //       };
      //     } else return { ...rect, selected: false };
      //   })
      // );
    }
    // TODO: get the times to update immediately upon assignment. This is not working at the moment.
    setForceUpdate((f) => f + 1);
  };

  const setSectionPause = () => {
    hideContextMenu();
    setRects(
      rects.map((rect) => {
        if (parseInt(selection.attrs.id) === rect.id) {
          return {
            ...rect,
            pauseAfter: !rect.pauseAfter,
          };
        } else return rect;
      })
    );
  };

  const handleKeyPress = (e) => {};

  const generateContextOptions = (e) => {
    setContextMenuOptions([
      {
        name: "Assign",
        icon: <RiMapPinTimeLine size={20} />,
        func: assignTime,
      },
      {
        name: "Toggle pause",
        icon: <GiPauseButton size={20} />,
        func: setSectionPause,
      },
    ]);
  };

  const showContextMenu = (e) => {
    e.evt.preventDefault();
    setListening(false);
    setContextMenu({
      ...contextMenu,
      x: stageRef.current.getPointerPosition().x,
      y: stageRef.current.getPointerPosition().y,
      visible: true,
    });
  };

  const hideContextMenu = () => {
    setListening(true);
    setContextMenu({
      ...contextMenu,
      visible: false,
    });
  };

  const [contextMenuOptions, setContextMenuOptions] = useState([]);

  return (
    <div className="canvas">
      <Stage
        scale={scale}
        ref={stageRef}
        width={canvasWidth}
        height={canvasHeight}
        onMouseDown={handleMouseDown}
        onKeyPress={handleKeyPress}
      >
        <Layer>
          <Image image={background} ref={bgRef} />
        </Layer>
        <Layer>
          {triggers &&
            triggers.map((trigger, index) => {
              if (trigger.type === "rect") {
                return (
                  <Rect
                    key={index}
                    x={trigger.x}
                    y={trigger.y}
                    width={trigger.width}
                    height={trigger.height}
                    fill="#00000040"
                    stroke={"gray"}
                    opacity={0.4}
                    strokeWidth={1}
                    dash={[4, 3]}
                  />
                );
              } else {
                return (
                  <Icon
                    id={trigger.id}
                    key={trigger.id}
                    x={trigger.x}
                    y={trigger.y}
                    iconUrl={trigger.iconUrl}
                    color={trigger.color}
                    scale={trigger.scale}
                    canvasWidth={canvasWidth}
                    canvasHeight={canvasHeight}
                    canvasScale={scale}
                    opacity={0.25}
                  />
                );
              }
            })}
        </Layer>
        <Layer>
          {rects &&
            rects.map((rect, index) => {
              return (
                <TimeRect
                  key={index}
                  index={index}
                  id={String(index)}
                  x={rect.x}
                  y={rect.y}
                  width={rect.width}
                  height={rect.height}
                  startTime={rect.startTime}
                  iterations={iterations}
                  workingIteration={workingIteration}
                  fill={colorPickerVisible ? readFill : rect.selected ? "#00db50" : "#03bafc"}
                  colorPickerVisible={colorPickerVisible}
                  readFill={readFill}
                  readOpacity={readOpacity}
                  rect={rect}
                />
              );
            })}
          {points &&
            points.map((point) => {
              return (
                <Icon
                  id={point.id}
                  key={point.id}
                  x={point.x}
                  y={point.y}
                  iconUrl={point.iconUrl}
                  // customText={point.customText}
                  color={point.color}
                  scale={point.scale}
                />
              );
            })}
          {contextMenu.visible && (
            <ContextMenu id="contextMenu" x={contextMenu.x} y={contextMenu.y} options={contextMenuOptions} />
          )}
        </Layer>
      </Stage>
    </div>
  );
};

export default TimeAssignCanvas;
